import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { EmbedPolicy } from '../models/embed-policy.model';

interface ParentConfigMessage {
  type: 'WHITE_LABEL_CONFIG';
  schemaVersion: 1;
  payload: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigSourceService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);

  async load(): Promise<unknown> {
    const windowRef = this.document.defaultView;

    if (!windowRef) {
      return {};
    }

    const parameters = new URLSearchParams(
      windowRef.location.search
    );

    const source =
      parameters.get('configSource')?.toLowerCase() ??
      'tenant';

    const policy = await this.loadEmbedPolicy();

    switch (source) {
      case 'parent':
        return this.waitForParentConfig(policy);

      case 'url':
        return this.loadRemoteConfig(parameters, policy);

      case 'tenant':
      default:
        return this.loadTenantConfig(parameters);
    }
  }

  private async loadTenantConfig(
    parameters: URLSearchParams
  ): Promise<unknown> {
    const tenant = this.sanitizeTenant(
      parameters.get('tenant') ?? 'default'
    );

    const url = `/config/brands/${tenant}.json`;

    return firstValueFrom(
      this.http.get<unknown>(url)
    );
  }

  private async loadRemoteConfig(
    parameters: URLSearchParams,
    policy: EmbedPolicy
  ): Promise<unknown> {
    const rawUrl = parameters.get('configUrl');

    if (!rawUrl) {
      throw new Error(
        'configUrl es obligatorio cuando configSource=url.'
      );
    }

    const windowRef = this.document.defaultView;

    if (!windowRef) {
      throw new Error(
        'No se encontró el contexto del navegador.'
      );
    }

    const url = new URL(
      rawUrl,
      windowRef.location.origin
    );

    const isSameOrigin =
      url.origin === windowRef.location.origin;

    const isAllowedRemoteOrigin =
      policy.allowedRemoteConfigOrigins.includes(
        '*'
      ) ||
      policy.allowedRemoteConfigOrigins.includes(
        url.origin
      );

    if (!isSameOrigin && !isAllowedRemoteOrigin) {
      throw new Error(
        `El origen ${url.origin} no está autorizado para cargar configuraciones.`
      );
    }

    return firstValueFrom(
      this.http.get<unknown>(url.toString())
    );
  }

  private waitForParentConfig(
    policy: EmbedPolicy
  ): Promise<unknown> {
    const windowRef = this.document.defaultView;

    if (!windowRef) {
      return Promise.reject(
        new Error('No existe window.')
      );
    }

    if (windowRef.parent === windowRef) {
      return Promise.reject(
        new Error(
          'configSource=parent requiere que la aplicación esté dentro de un iframe.'
        )
      );
    }

    return new Promise<unknown>((resolve, reject) => {
      const timeout = windowRef.setTimeout(() => {
        cleanup();

        reject(
          new Error(
            'No se recibió configuración desde la página padre.'
          )
        );
      }, policy.parentConfigTimeoutMs);

      const messageHandler = (
        event: MessageEvent<unknown>
      ): void => {
        const isWildcardOrigin =
          policy.allowedParentOrigins.includes('*');

        if (
          !isWildcardOrigin &&
          !policy.allowedParentOrigins.includes(
            event.origin
          )
        ) {
          return;
        }

        if (!this.isParentConfigMessage(event.data)) {
          return;
        }

        cleanup();
        resolve(event.data.payload);
      };

      const cleanup = (): void => {
        windowRef.clearTimeout(timeout);
        windowRef.removeEventListener(
          'message',
          messageHandler
        );
      };

      windowRef.addEventListener(
        'message',
        messageHandler
      );

      const expectedParentOrigin =
        this.resolveExpectedParentOrigin(
          policy.allowedParentOrigins
        );

      /*
       * WHITE_LABEL_READY no contiene información sensible.
       * La configuración entrante se sigue validando mediante event.origin.
       */
      windowRef.parent.postMessage(
        {
          type: 'WHITE_LABEL_READY',
          schemaVersion: 1
        },
        expectedParentOrigin ?? '*'
      );
    });
  }

  private resolveExpectedParentOrigin(
    allowedOrigins: string[]
  ): string | undefined {
    const referrer = this.document.referrer;

    if (!referrer) {
      return undefined;
    }

    try {
      const referrerOrigin = new URL(referrer).origin;

      return allowedOrigins.includes('*') ||
        allowedOrigins.includes(referrerOrigin)
        ? referrerOrigin
        : undefined;
    } catch {
      return undefined;
    }
  }

  private isParentConfigMessage(
    value: unknown
  ): value is ParentConfigMessage {
    if (
      typeof value !== 'object' ||
      value === null ||
      Array.isArray(value)
    ) {
      return false;
    }

    const candidate =
      value as Partial<ParentConfigMessage>;

    return (
      candidate.type === 'WHITE_LABEL_CONFIG' &&
      candidate.schemaVersion === 1 &&
      'payload' in candidate
    );
  }

  private sanitizeTenant(value: string): string {
    const normalized = value
      .trim()
      .toLowerCase();

    const isValid = /^[a-z0-9-]+$/.test(
      normalized
    );

    if (!isValid) {
      throw new Error(
        'El identificador del tenant contiene caracteres inválidos.'
      );
    }

    return normalized;
  }

  private async loadEmbedPolicy(): Promise<EmbedPolicy> {
    try {
      const policy = await firstValueFrom(
        this.http.get<Partial<EmbedPolicy>>(
          '/config/embed-policy.json'
        )
      );

      return {
        allowedParentOrigins:
          policy.allowedParentOrigins?.filter(
            this.isValidOrigin
          ) ?? [],

        allowedRemoteConfigOrigins:
          policy.allowedRemoteConfigOrigins?.filter(
            this.isValidOrigin
          ) ?? [],

        parentConfigTimeoutMs:
          this.normalizeTimeout(
            policy.parentConfigTimeoutMs
          )
      };
    } catch {
      return {
        allowedParentOrigins: [],
        allowedRemoteConfigOrigins: [],
        parentConfigTimeoutMs: 5000
      };
    }
  }

  private readonly isValidOrigin = (
    value: string
  ): boolean => {
    if (value === '*') {
      return true;
    }

    try {
      const url = new URL(value);

      return (
        url.origin === value &&
        (
          url.protocol === 'https:' ||
          url.hostname === 'localhost' ||
          url.hostname === '127.0.0.1'
        )
      );
    } catch {
      return false;
    }
  };

  private normalizeTimeout(
    value: number | undefined
  ): number {
    if (
      typeof value !== 'number' ||
      !Number.isFinite(value)
    ) {
      return 5000;
    }

    return Math.min(
      Math.max(value, 1000),
      15000
    );
  }
}
